import React from 'react';
import { twMerge } from 'tailwind-merge';

export const addStyles = (input: string | (string | undefined)[]) => {
  const styles: string[] = [];

  // make a string of CSS classes unique
  const splitAndFilter = (str: string) => {
    for (const cssClass of str.split(' ')) {
      if (!styles.includes(cssClass)) {
        styles.push(cssClass.trim());
      }
    }
  };

  if (Array.isArray(input)) {
    // remove undefined values from the array
    for (const str of input.filter((i) => typeof i === 'string')) {
      if (str) splitAndFilter(str);
    }
  } else {
    splitAndFilter(input);
  }

  // combined the unique array back into a single string
  return styles.join(' ');
};

/**
 * Merges multiple tailwind CSS class names into a single string or combines multiple arrays of class names into a
 * single array. In contrast to addStyles, it ensures that the resulting class names are unique and sorted in
 * alphabetical order for readability while overriding any tailwind styles. It does so by using the tailwind-merge
 * package to merge the tailwind CSS class names such that the CSS class names of a given kind appearing later override
 * the ones appearing earlier. e.g. mergeStyles(['text-lg', 'text-sm', 'text-md']) will result into 'text-md'.
 *
 * @param {string | string[]} input - The CSS class names to be merged. It can be a single string of class names
 * separated by spaces, or an array of such strings.
 *
 * @returns {string} A string of unique, sorted CSS class names separated by spaces.
 *
 * @example
 * mergeStyles('flex flex-row flex flex-row'); // returns 'flex flex-row'
 * mergeStyles('p-6 flex bg-color-red flex-row'); // returns 'bg-color-red flex flex-row p-6'
 * mergeStyles(['flex flex-col', 'bg-primary', 'text-2xl']); // returns 'bg-primary flex flex-col text-2xl'
 * mergeStyles(['flex flex-col', 'bg-primary flex flex-col', 'text-2xl flex']);
 * // returns 'bg-primary flex flex-col text-2xl'
 */
export const mergeStyles = (input: string | (string | undefined)[]) => {
  let mergedStyles = '';

  if (Array.isArray(input)) {
    for (const str of input) {
      mergedStyles = twMerge(mergedStyles, str?.trim() ?? '');
    }
  } else {
    mergedStyles = input?.trim() ?? '';
  }

  // twMerge call wont remove duplicates from input strings already having duplicates
  // e.g. twMerge('text-red-500 text-red-500') will return 'text-red-500 text-red-500'
  // additionally we want to sort the classes in alphabetical order for readability and ease of testing
  const uniqueInOrderStyles = [...new Set(mergedStyles.split(' '))].sort((a, b) => a.localeCompare(b)).join(' ');
  return uniqueInOrderStyles;
};

export const useChildrenWithProps = <PropType>(
  children: React.ReactNode,
  newProps: Partial<PropType>,
  indexFieldName?: string,
  indexFieldOffset = 0
) => {
  return React.useMemo(() => {
    return React.Children.map(children, (child, index) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(
          child as React.ReactElement<PropType>,
          indexFieldName === undefined ? newProps : { ...newProps, [indexFieldName]: index + indexFieldOffset }
        );
      }
    });
  }, [children, newProps, indexFieldName, indexFieldOffset]);
};

export const setInputValue = (element: HTMLInputElement, value: unknown) => {
  // This is for manually triggering onChange event for an input
  setHTMLObjectValue(window.HTMLInputElement.prototype, element, value);
  const ev2 = new InputEvent('input', { bubbles: true });
  element.dispatchEvent(ev2);
};

export const setHTMLObjectValue = (prototype: HTMLElement, element: HTMLElement, value: unknown) => {
  // React overrides the setter property, and this allows us to get around that
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  nativeInputValueSetter?.call(element, value);
};

export const useWindowSize = () => {
  const [size, setSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const handleResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });

  React.useLayoutEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return size;
};

export const debounce = <T extends (...args: Parameters<T>) => ReturnType<T>>(fn: T, ms = 300) => {
  let timeout: ReturnType<typeof setTimeout>;
  return function (...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), ms);
  };
};

export const portalElement = () => document.querySelector('#dsl-portal') || document.body;

export const stopMousePropagation = <T>(e: React.MouseEvent<T>) => e.stopPropagation();

export const stopKeyboardEventHandler = <T>(e: React.KeyboardEvent<T>) => e.stopPropagation();

// rewrite to decrease the complexity of
// toVal function from https://github.com/lukeed/clsx/blob/master/src/index.js
export function toVal(mix: any): string {
  if (typeof mix === 'string' || typeof mix === 'number') {
    return String(mix);
  }

  if (Array.isArray(mix)) {
    return mix
      .map((item) => item && toVal(item))
      .filter(Boolean)
      .join(' ');
  }

  if (typeof mix === 'object' && mix !== null) {
    return Object.keys(mix)
      .filter((key) => mix[key])
      .join(' ');
  }

  return '';
}

export function clsx(...args: any[]): string {
  let i = 0,
    tmp,
    x,
    str = '';
  const len = args.length;
  for (; i < len; i++) {
    tmp = args[i];
    if (tmp && (x = toVal(tmp))) {
      str && (str += ' ');
      str += x;
    }
  }
  return str;
}

export function cn(...inputs: Parameters<typeof clsx>) {
  return twMerge(clsx(...inputs));
}
